import React, { Component } from 'react';
import { bool, object, shape, arrayOf, string } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import { areAllFacetsChecked, getQueryParams } from '../../lib/facetHelpers';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import CollapsingColumnLayout from '../Layouts/CollapsingColumn';
import SearchResults from './SearchResults/SearchResults';
import ResultDetails from './ResultDetails/ResultDetails';
import FacetPanel from 'ps-components/FacetPanel/FacetPanel';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import getHiddenFacets from 'ps-components/FacetPanel/getHiddenFacets';
import './JobMatchingContainer.scss';

import * as slideoutActionCreators from 'ps-components/Slideout/SlideoutReducer';
import { jobFacetSearchActions } from '../../reducers/modules/jobs/jobFacetSearch/jobFacetSearch';
import * as orgJobsActionCreators from './OrgJobsActions';
import * as surveyJobsActionCreators from './SurveyJobsActions';

class JobMatching extends Component {
  static propTypes = {
    actions: object,
    location: object,
    match: object,
    selectedOrgJob: object,
    orgJobsIsSelected: bool,
    orgJobsSearchText: string,
    surveyJobs: object,
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    useJobCodeKey: bool
  };

  constructor(props) {
    super(props);
    this.state = { showSearch: true };
  }

  componentDidMount() {
    const { actions } = this.props;
    actions.searchInputSearch('');
  }

  componentWillUnmount() {
    const { actions } = this.props;
    this.handleReset();
    this.toggleDetail();
    actions.clearSelectedJobs();
  }

  _pageOrgJobs = (first, newAfter) => {
    const { actions } = this.props;

    actions.pageList(first, newAfter);
  };

  toggleDetail = () => {
    const { actions } = this.props;
    const { showSearch } = this.state;
    this.setState({ showSearch: !showSearch });
  };

  handleReset = () => {
    const { actions, currency, jobFacetSearch } = this.props;

    actions.search({
      params: getQueryParams(jobFacetSearch.name),
      clear: true
    });
    actions.setOrgJobFacetText('');
  };

  render() {
    const {
      actions,
      selectedOrgJob,
      orgJobsIsSelected,
      orgJobsSearchText,
      replacementTokens,
      surveyJobs,
      useJobCodeKey,
      jobFacetSearch
    } = this.props;
    const { showSearch } = this.state;
    const detailFlex = !showSearch ? 5 : 1;
    const resultsFlex = showSearch ? 5 : 0;
    const selectedJobConnection = selectedOrgJob && selectedOrgJob.jobMatches ? selectedOrgJob.jobMatches : [];
    const searchResultsClass = showSearch ? 'secondary__panel' : 'primary__panel';
    const hasFacetItems = jobFacetSearch.facets
      .map(facet => facet.items)
      .reduce((memo, items) => memo.concat(items), []).length;
    const hasOrgJobs = jobFacetSearch.items.length;

    if (jobFacetSearch.loading && !hasOrgJobs && !hasFacetItems) {
      return <InPlaceIndicator />;
    }

    const hasSearchCriteria =
      compact(flatten(Object.values(jobFacetSearch.include))).length > 0 ||
      compact(flatten(Object.values(jobFacetSearch.exclude))).length > 0;

    if (!hasOrgJobs && !hasFacetItems && !hasSearchCriteria && jobFacetSearch.loaded) {
      return (
        <div className="result__details-container">
          <div className="details">
            <Placeholder
              className="pxl-text-small pxl-text-muted"
              imagePath="/view/static/mp/images/empty_results.svg"
              primaryText="No org jobs exist for this currency or group."
              secondaryText="Please select another currency or group."
              showText={true}
            />
          </div>
        </div>
      );
    }

    const showResetButton =
      areAllFacetsChecked(jobFacetSearch.facets, jobFacetSearch.facetSelection) ||
      (jobFacetSearch.lastQueryParams && jobFacetSearch.lastQueryParams.es_query !== '');

    return (
      <CollapsingColumnLayout
        className="job__matching-container"
        render={({ CollapseColumn, Column, Wrapper }) => (
          <Wrapper>
            <CollapseColumn show={showSearch}>
              <div className="org__job-facet__panel-container">
                <FacetPanel
                  className="job-matching org__job-facets"
                  facetItemClick={actions.facetItemClick}
                  facetReset={actions.facetReset}
                  facets={jobFacetSearch.facets}
                  facetSelection={jobFacetSearch.facetSelection}
                  hiddenFacets={getHiddenFacets('jobMatching', useJobCodeKey)}
                  isDisabled={!orgJobsIsSelected}
                  isLoaded={jobFacetSearch.loaded}
                  lastFacetFilteredID={jobFacetSearch.lastFacetFilteredID}
                  limit={5}
                  resetSearch={this.handleReset}
                  showFacetSectionSearch={true}
                  showResetButton={showResetButton}
                  showZeros={jobFacetSearch.showZeros}
                  title="Job Matching"
                />
              </div>
            </CollapseColumn>
            <Column flex={resultsFlex}>
              <SearchResults
                className={searchResultsClass}
                clearSelectedJobs={actions.clearSelectedJobs}
                hasResults={!hasOrgJobs && !hasFacetItems}
                isSelected={surveyJobs.isSelected}
                offset={jobFacetSearch.offset}
                orgJobs={jobFacetSearch.items}
                pageOrgJobs={this._pageOrgJobs}
                replacementTokens={replacementTokens}
                search={actions.searchInputSearch}
                searchId="org__jobs-search-job-matching"
                searchText={orgJobsSearchText}
                selectedJob={selectedOrgJob}
                setSearchText={actions.setOrgJobFacetText}
                setSelectedJob={actions.setSelectedJob}
                showSearch={showSearch}
                toggleDetail={this.toggleDetail}
                toggleShowSelectedSurveyJobs={actions.toggleShowSelectedSurveyJobs}
                totalCount={jobFacetSearch.searchResultsCount}
                useJobCodeKey={useJobCodeKey}
              />
            </Column>
            <Column flex={detailFlex}>
              {selectedOrgJob.id ? (
                <ResultDetails
                  createJobMatch={actions.createJobMatch}
                  deleteJobMatch={actions.deleteJobMatch}
                  isSelected={surveyJobs.isSelected}
                  replacementTokens={replacementTokens}
                  searchText={surveyJobs.searchText}
                  selectedJob={selectedOrgJob}
                  selectedJobConnection={selectedJobConnection}
                  setSearchText={actions.setSurveyJobFacetText}
                  showText={false}
                  toggleShowSelectedSurveyJobs={actions.toggleShowSelectedSurveyJobs}
                  useJobCodeKey={useJobCodeKey}
                />
              ) : (
                <div className="details">
                  <Placeholder
                    className="pxl-text-small pxl-text-muted"
                    imagePath="/view/static/mp/images/empty_results.svg"
                    secondaryText="Select Job to Start Matching"
                    showText={true}
                  />
                </div>
              )}
            </Column>
          </Wrapper>
        )}
      />
    );
  }
}

const mapStateToProps = ({ orgJobs, surveyJobs, slideout, session, jobFacetSearch }) => ({
  selectedOrgJob: orgJobs.selectedJob,
  orgJobsIsSelected: orgJobs.isSelected,
  orgJobsSearchText: orgJobs.searchText,
  surveyJobs,
  slideout,
  replacementTokens: session.replacementTokens,
  useJobCodeKey: session.organization.useJobCodeKey,
  jobFacetSearch
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    { ...orgJobsActionCreators, ...surveyJobsActionCreators, ...slideoutActionCreators, ...jobFacetSearchActions },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(JobMatching);
